/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  EmbeddedViewRef,
  Inject,
  Injectable,
  Injector,
  TemplateRef,
  Type
} from '@angular/core';
import { ModalComponent } from '../components/modal/modal.component';
import { ModalInjector } from '../components/modal/modal-injector';
import { ModalConfig } from '../components/modal/modal-config';
import { ModalRef } from '../components/modal/modal-ref';
import { DOCUMENT } from '@angular/common';
declare let $: any;

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private modalComponentRefs = new Map<string, ComponentRef<ModalComponent>>();

  constructor(
    @Inject(DOCUMENT) private document,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  public openModal(embedType: string | TemplateRef<any> | Type<any>, config: ModalConfig): ModalRef {
    config.identifier = new Date().getTime() + '_' + this.modalComponentRefs.size;
    config.contentType = this.getContentType(embedType, config);
    const modalRef = this.createModalComponent(config);

    if (embedType instanceof Type) {
      this.modalComponentRefs.get(config.identifier).instance.childComponentType = embedType;
    }
    modalRef['instance'] = this.modalComponentRefs.get(config.identifier).instance;

    return modalRef;
  }

  private createModalComponent(config: ModalConfig): ModalRef {
    const map = new WeakMap();
    map.set(ModalConfig, config);

    const modalRef = new ModalRef();
    map.set(ModalRef, modalRef);

    const sub = modalRef.afterClosed.subscribe(() => {
      this.removeModalComponent(config.identifier);
      sub.unsubscribe();
    });

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(ModalComponent);
    const componentRef = componentFactory.create(new ModalInjector(this.injector, map));

    this.appRef.attachView(componentRef.hostView);

    const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    this.document.body.appendChild(domElem);

    componentRef.instance.onClose.subscribe(() => {
      this.removeModalComponent(config.identifier);
    });

    this.modalComponentRefs.set(config.identifier, componentRef);

    return modalRef;
  }

  private removeModalComponent(modalIdentifier) {
    const modalComponentRef = this.modalComponentRefs.get(modalIdentifier);
    this.appRef.detachView(modalComponentRef.hostView);
    modalComponentRef.destroy();
    this.modalComponentRefs.delete(modalIdentifier);
  }

  private getContentType(content: string | TemplateRef<any> | Type<any>, config: ModalConfig) {
    if (typeof content === 'string') {
      config.content = content;
      return 'string';
    } else if (content instanceof TemplateRef) {
      config.content = content;
      return 'template';
    } else {
      return 'component';
    }
  }
  isModalOpen() {
    return this.modalComponentRefs.size != 0;
  }
  isAngularJsModalOpen() {
    const modalElement = $('div[uib-modal-window="modal-window"]');
    return modalElement.length > 0 || modalElement.data('$uibModalInstance');
  }
}
